import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { useAuth } from '../auth'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { thaiDateTime } from '../lib/format'

/**
 * Zero-trust microsegmentation at the edge (part 73).
 *
 * The screen shows the authored rules, the compiled document that would be published and the
 * append-only history of real Cloudflare KV writes. "คอมไพล์ตัวอย่าง" is a read-only preview;
 * "ผลักขึ้น KV" performs the external write and records the attempt, success or failure.
 *
 * The page is not routed from App.tsx in this part, matching the unmounted API group. Wiring is
 * one import and one <Route>, reviewed together with the Worker's evaluatePolicy call site.
 */

type Effect = 'allow' | 'deny'
type PushStatus = 'succeeded' | 'failed'

interface Rule {
  id: string
  tenant_id: string
  name: string
  description: string
  effect: Effect
  priority: number
  enabled: boolean
  path_prefix: string
  path_exact: string
  methods: string[]
  roles: string[]
  source_cidrs: string[]
  tenant_ids: string[]
  created_by?: string
  created_at: string
  updated_at: string
}

interface CompiledRule {
  id: string
  name: string
  effect: Effect
  priority: number
  path_prefix: string
  path_exact: string
  methods: string[]
  roles: string[]
  source_cidrs: string[]
  tenant_ids: string[]
}

interface PolicyDocument {
  schema: string
  tenant_id: string
  generated_at: string
  algorithm: string
  version: string
  hash: string
  default_effect: Effect
  static_asset_allow: boolean
  protected_prefixes: string[]
  rules: CompiledRule[]
  stats: { rules: number; allow: number; deny: number }
  warnings: string[]
}

interface PushRow {
  id: string
  version: string
  document_hash: string
  kv_key: string
  kv_namespace_id: string
  kv_mode: 'wrangler' | 'api'
  status: PushStatus
  error_th: string
  rules_total: number
  allow_rules: number
  deny_rules: number
  pushed_by?: string
  pushed_by_role: string
  created_at: string
}

interface PushResult {
  push: PushRow
  document: PolicyDocument
  recorded: boolean
  warning_th?: string
}

const EFFECT_LABEL: Record<Effect, string> = { allow: 'อนุญาต', deny: 'ปฏิเสธ' }
const EFFECT_TONE: Record<Effect, string> = {
  allow: 'var(--color-emerald-soft)',
  deny: 'var(--color-crimson)',
}
const STATUS_LABEL: Record<PushStatus, string> = { succeeded: 'สำเร็จ', failed: 'ล้มเหลว' }
const STATUS_TONE: Record<PushStatus, string> = {
  succeeded: 'var(--color-emerald-soft)',
  failed: 'var(--color-crimson)',
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

function pathLabel(rule: { path_prefix: string; path_exact: string }): string {
  return rule.path_exact || rule.path_prefix || '—'
}

function listLabel(values: string[], empty: string): string {
  return values.length > 0 ? values.join(', ') : empty
}

export function Enforcement() {
  const { me } = useAuth()
  const canPush = me?.role === 'owner' || me?.role === 'admin'
  const queryClient = useQueryClient()

  const [notice, setNotice] = useState('')
  const [preview, setPreview] = useState<PolicyDocument | null>(null)

  const rules = useQuery({
    queryKey: ['enforcement-rules'],
    queryFn: () => api<Rule[]>('/v1/enforcement/rules'),
  })

  const pushes = useQuery({
    queryKey: ['enforcement-pushes'],
    queryFn: () => api<PushRow[]>('/v1/enforcement/pushes?limit=30'),
  })

  const compile = useMutation({
    mutationFn: () => api<PolicyDocument>('/v1/enforcement/compile'),
    onSuccess: (doc) => {
      setPreview(doc)
      setNotice(`คอมไพล์แล้ว ${doc.stats.rules} กฎ · เวอร์ชัน ${doc.version} · ยังไม่มีการเขียนไปยัง KV`)
    },
    onError: (err) => setNotice(errorText(err, 'คอมไพล์นโยบายไม่สำเร็จ')),
  })

  const push = useMutation({
    mutationFn: () => apiWrite<PushResult>('/v1/enforcement/push', 'POST', {}),
    onSuccess: (result) => {
      setPreview(result.document)
      const recorded = result.recorded ? '' : ' · บันทึกประวัติไม่สำเร็จ'
      const warning = result.warning_th ? ` · ${result.warning_th}` : ''
      setNotice(`ผลักเวอร์ชัน ${result.document.version} สำเร็จผ่าน ${result.push.kv_mode} · ${result.push.rules_total} กฎ${recorded}${warning}`)
      void queryClient.invalidateQueries({ queryKey: ['enforcement-pushes'] })
    },
    onError: (err) => {
      setNotice(errorText(err, 'ผลักนโยบายไปยัง KV ไม่สำเร็จ'))
      void queryClient.invalidateQueries({ queryKey: ['enforcement-pushes'] })
    },
  })

  const rows = rules.data ?? []
  const enabled = rows.filter((rule) => rule.enabled)
  const denyCount = enabled.filter((rule) => rule.effect === 'deny').length
  const allowCount = enabled.filter((rule) => rule.effect === 'allow').length
  const latest = pushes.data?.[0]

  return (
    <>
      <PageHead
        title="การบังคับใช้นโยบายที่ Edge"
        sub="กฎขององค์กรถูกคอมไพล์เป็นเอกสารเดียวแล้วผลักไปยัง Cloudflare KV · deny ชนะ allow เสมอ · API ที่ไม่ตรงกฎถูกปฏิเสธ แต่ไฟล์ Static ได้รับอนุญาตตามค่าเริ่มต้น"
        actions={(
          <div className="row" style={{ gap: 8 }}>
            <button
              className="btn"
              onClick={() => { setNotice(''); compile.mutate() }}
              disabled={compile.isPending || push.isPending}
            >
              {compile.isPending ? 'กำลังคอมไพล์…' : 'คอมไพล์ตัวอย่าง'}
            </button>
            {canPush ? (
              <button
                className="btn btn-primary"
                onClick={() => { setNotice(''); push.mutate() }}
                disabled={push.isPending || compile.isPending}
              >
                {push.isPending ? 'กำลังผลัก…' : 'ผลักขึ้น KV'}
              </button>
            ) : null}
          </div>
        )}
      />

      <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <span className="chip chip-on">KV namespace · CONFIG</span>
        <span className="chip">คีย์แยกตาม tenant: enforcement:policy:&lt;tenant&gt;</span>
        <span className="chip">ค่าเริ่มต้น API · {preview?.default_effect === 'allow' ? 'อนุญาต' : 'ปฏิเสธ'}</span>
        <span className="chip">Static · {preview && preview.static_asset_allow ? 'อนุญาต' : 'ตามเอกสาร'}</span>
      </div>

      {notice ? (
        <div className="card card-pad" role="status" style={{ marginBottom: 16 }}>
          <span className="dim" style={{ fontSize: 12.5 }}>{notice}</span>
        </div>
      ) : null}

      <div className="grid-4" style={{ marginBottom: 16 }}>
        <Stat
          label="กฎที่เปิดใช้งาน"
          value={enabled.length}
          unit={`/ ${rows.length}`}
          foot={`คอมไพล์ล่าสุด ${preview?.stats.rules ?? latest?.rules_total ?? 0} กฎ`}
        />
        <Stat label="กฎปฏิเสธ" value={denyCount} foot="มีลำดับความสำคัญเหนือ allow ทุกข้อ" />
        <Stat label="กฎอนุญาต" value={allowCount} foot="ใช้เมื่อไม่มี deny ใดตรงกับคำขอ" />
        <Stat
          label="เวอร์ชันที่ใช้งาน"
          value={<span className="mono" style={{ fontSize: 16 }}>{preview?.version ?? latest?.version ?? '—'}</span>}
          foot={latest ? `ผลักล่าสุด ${thaiDateTime(latest.created_at)} · ${STATUS_LABEL[latest.status]}` : 'ยังไม่เคยผลัก'}
        />
      </div>

      <div className="card card-pad" style={{ marginBottom: 16 }}>
        <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 10 }}>
          <div className="card-label">กฎบังคับใช้ขององค์กร</div>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <span className="chip">Method ว่าง = ทุก method</span>
            <span className="chip">Role ว่าง = ทุก role</span>
            <span className="chip">CIDR ว่าง = ทุก IP</span>
          </div>
        </div>

        {rules.isLoading ? (
          <div className="stack"><Skel h={38} /><Skel h={38} /><Skel h={38} /></div>
        ) : rows.length === 0 ? (
          <Empty
            title="ยังไม่มีกฎบังคับใช้"
            body="เพิ่มกฎใน platform.enforcement_rules แล้วคอมไพล์ เอกสารที่ไม่มีกฎจะปฏิเสธทุกเส้นทาง API และอนุญาตเฉพาะไฟล์ Static"
          />
        ) : (
          <div className="table-wrap" style={{ overflowX: 'auto' }}>
            <table className="table data">
              <thead>
                <tr>
                  <th>ผล</th>
                  <th>กฎ</th>
                  <th>เส้นทาง</th>
                  <th>Method</th>
                  <th>Role</th>
                  <th>Source CIDR</th>
                  <th className="num">ลำดับ</th>
                  <th>สถานะ</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((rule) => (
                  <tr key={rule.id}>
                    <td>
                      <span className="sev" style={{ color: EFFECT_TONE[rule.effect] }}>
                        <i className="sev-dot" style={{ background: EFFECT_TONE[rule.effect] }} />
                        {EFFECT_LABEL[rule.effect]}
                      </span>
                    </td>
                    <td style={{ maxWidth: 300, whiteSpace: 'normal' }}>
                      <div>{rule.name}</div>
                      <div className="dim mono" style={{ fontSize: 10.5, marginTop: 3 }}>
                        {rule.id} · tenant ของเอกสาร
                      </div>
                    </td>
                    <td className="mono" style={{ fontSize: 11.5 }}>{pathLabel(rule)}</td>
                    <td className="dim" style={{ fontSize: 12 }}>{listLabel(rule.methods, 'ทุก method')}</td>
                    <td className="dim" style={{ fontSize: 12 }}>{listLabel(rule.roles, 'ทุก role')}</td>
                    <td className="dim mono" style={{ fontSize: 11 }}>{listLabel(rule.source_cidrs, 'ทุก IP')}</td>
                    <td className="num tnum">{rule.priority}</td>
                    <td>
                      <span className="chip" style={{ color: rule.enabled ? 'var(--color-emerald-soft)' : 'var(--color-ivory-dim)' }}>
                        {rule.enabled ? 'เปิด' : 'ปิด'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="grid-main" style={{ marginBottom: 16 }}>
        <div className="card card-pad">
          <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 10 }}>
            <div className="card-label">เอกสารที่คอมไพล์</div>
            {preview ? (
              <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                <span className="chip chip-on mono">{preview.version}</span>
                <span className="chip">{preview.stats.deny} ปฏิเสธ · {preview.stats.allow} อนุญาต</span>
              </div>
            ) : null}
          </div>

          {!preview ? (
            <Empty
              title="ยังไม่ได้คอมไพล์ในหน้านี้"
              body="กด “คอมไพล์ตัวอย่าง” เพื่ออ่านกฎจากฐานข้อมูลและสร้างเอกสารโดยไม่เขียนไปยัง KV"
            />
          ) : (
            <>
              <div className="row" style={{ gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
                <span className="dim" style={{ fontSize: 12 }}>
                  hash <span className="mono">{preview.hash}</span>
                </span>
                <span className="dim" style={{ fontSize: 12 }}>สร้างเมื่อ {thaiDateTime(preview.generated_at)}</span>
                <span className="dim" style={{ fontSize: 12 }}>schema {preview.schema}</span>
              </div>
              {preview.warnings.length > 0 ? (
                <div className="card card-pad" style={{ marginBottom: 12, borderColor: 'var(--color-amber)' }}>
                  {preview.warnings.map((warning) => (
                    <div key={warning} className="dim" style={{ fontSize: 12.5 }}>{warning}</div>
                  ))}
                </div>
              ) : null}

              {preview.rules.length === 0 ? (
                <Empty
                  title="เอกสารไม่มีกฎ"
                  body="นี่คือสถานะที่ปลอดภัย: ทุกเส้นทาง API จะถูกปฏิเสธ และไฟล์ Static จะได้รับอนุญาตตามค่าเริ่มต้น"
                />
              ) : (
                <div className="table-wrap" style={{ overflowX: 'auto', marginBottom: 12 }}>
                  <table className="table data">
                    <thead>
                      <tr>
                        <th className="num">#</th>
                        <th>ผล</th>
                        <th>เส้นทาง</th>
                        <th>Method</th>
                        <th>Role</th>
                        <th>CIDR</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.rules.map((rule, index) => (
                        <tr key={rule.id}>
                          <td className="num tnum">{index + 1}</td>
                          <td style={{ color: EFFECT_TONE[rule.effect] }}>{EFFECT_LABEL[rule.effect]}</td>
                          <td className="mono" style={{ fontSize: 11 }}>{pathLabel(rule)}</td>
                          <td className="dim" style={{ fontSize: 11.5 }}>{listLabel(rule.methods, 'ทุก')}</td>
                          <td className="dim" style={{ fontSize: 11.5 }}>{listLabel(rule.roles, 'ทุก')}</td>
                          <td className="dim mono" style={{ fontSize: 10.5 }}>{listLabel(rule.source_cidrs, 'ทุก')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="card-label" style={{ marginBottom: 6 }}>JSON ที่จะเขียนไปยัง KV</div>
              <pre
                className="mono"
                style={{
                  margin: 0,
                  maxHeight: 300,
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  fontSize: 11,
                  lineHeight: 1.55,
                }}
              >
                {JSON.stringify(preview, null, 2)}
              </pre>
            </>
          )}
        </div>

        <div className="card card-pad">
          <div className="card-label" style={{ marginBottom: 10 }}>ความหมายของเอกสาร</div>
          <div className="stack" style={{ fontSize: 12.5, lineHeight: 1.65 }}>
            <div>
              <div style={{ color: 'var(--color-gold)' }}>Deny ชนะทุกอย่าง</div>
              <div className="dim">ถ้ากฎปฏิเสธข้อใดตรงกับคำขอ ผลคือปฏิเสธ แม้กฎอนุญาตจะมีลำดับดีกว่า</div>
            </div>
            <div>
              <div style={{ color: 'var(--color-gold)' }}>ค่าเริ่มต้น API</div>
              <div className="dim">{preview?.default_effect === 'allow' ? 'อนุญาต' : 'ปฏิเสธ'} เมื่อไม่มีกฎใดตรงกับเส้นทางใต้ {preview?.protected_prefixes.join(', ') ?? '/v1, /edge, /metrics'}</div>
            </div>
            <div>
              <div style={{ color: 'var(--color-gold)' }}>Static</div>
              <div className="dim">{preview?.static_asset_allow ? 'ไฟล์ HTML, JS, CSS และรูปภาพได้รับอนุญาตโดยไม่ต้องมีกฎ' : 'เอกสารนี้ไม่เปิดค่าเริ่มต้นสำหรับ Static'}</div>
            </div>
            <div>
              <div style={{ color: 'var(--color-gold)' }}>Tenant</div>
              <div className="dim">ทุกกฎผูกกับ tenant ของเอกสาร คำขอที่ tenant ไม่ตรงจะถูกปฏิเสธก่อนประเมินกฎ</div>
            </div>
          </div>
        </div>
      </div>

      <div className="card card-pad">
        <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 10 }}>
          <div className="card-label">ประวัติการผลักไปยัง KV</div>
          {latest ? <span className="dim mono" style={{ fontSize: 11.5 }}>{latest.kv_namespace_id}</span> : null}
        </div>

        {pushes.isLoading ? (
          <div className="stack"><Skel h={34} /><Skel h={34} /><Skel h={34} /></div>
        ) : (pushes.data ?? []).length === 0 ? (
          <Empty title="ยังไม่มีการผลัก" body="การผลักแต่ละครั้งจะถูกบันทึกพร้อมเวอร์ชัน คีย์ และผลลัพธ์ แม้การเขียนจะล้มเหลว" />
        ) : (
          <div className="table-wrap" style={{ overflowX: 'auto' }}>
            <table className="table data">
              <thead>
                <tr>
                  <th>เวลา</th>
                  <th>สถานะ</th>
                  <th>เวอร์ชัน</th>
                  <th>KV key</th>
                  <th>โหมด</th>
                  <th className="num">กฎ</th>
                  <th>ผู้ผลัก</th>
                </tr>
              </thead>
              <tbody>
                {(pushes.data ?? []).map((row) => (
                  <tr key={row.id}>
                    <td className="dim" style={{ fontSize: 12 }}>{thaiDateTime(row.created_at)}</td>
                    <td style={{ color: STATUS_TONE[row.status] }}>{STATUS_LABEL[row.status]}</td>
                    <td className="mono" style={{ fontSize: 11.5 }}>{row.version}</td>
                    <td className="mono" style={{ fontSize: 10.5, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {row.kv_key}
                    </td>
                    <td className="dim" style={{ fontSize: 12 }}>{row.kv_mode}</td>
                    <td className="num tnum">{row.allow_rules} / {row.deny_rules}</td>
                    <td className="dim" style={{ fontSize: 12 }}>{row.pushed_by_role || 'ระบบ'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {(pushes.data ?? []).some((row) => row.status === 'failed') ? (
          <div className="dim" style={{ fontSize: 11.5, marginTop: 10 }}>
            แถวที่ล้มเหลวเก็บข้อความสาเหตุไว้ในเอกสารผลัก และไม่ได้หมายความว่า Worker ใช้งานเวอร์ชันนั้น
          </div>
        ) : null}
      </div>
    </>
  )
}
